// Mendelian genetics (GENETICS.md §2). Pure functions — same inputs, same
// offspring, so the Punnett-square preview and the real hatch always agree.
//
// Educational accuracy is non-negotiable here: blue (O) is DOMINANT, and brown
// is a separate coating painted over the base shell. Blue + brown = olive.

import { clamp, round2 } from '../core/math';
import type { Rng } from '../core/rng';
import type {
  Breed,
  BroodinessAllele,
  EggColorAllele,
  EggShellColor,
  Genotype,
  PlumageAllele,
  TemperamentAllele,
  ToeAllele,
} from '../core/types';
import { breedProfile, getConfig, type Profile } from './config';

type AllelePair<T extends string> = [T, T];

/** One allele from each parent, with a small chance of a mutation flip. */
export function crossPair<T extends string>(
  sire: AllelePair<T>,
  dam: AllelePair<T>,
  dominant: T,
  recessive: T,
  rng: Rng,
  mutationChance: number,
): AllelePair<T> {
  const pick = (pair: AllelePair<T>): T => pair[rng.int(0, 1)];
  const flip = (allele: T): T =>
    rng.chance(mutationChance) ? (allele === dominant ? recessive : dominant) : allele;
  return [flip(pick(sire)), flip(pick(dam))];
}

export function crossGenotypes(
  sire: Genotype,
  dam: Genotype,
  rng: Rng,
  profile: Profile = 'normal',
): Genotype {
  const mutation = getConfig(profile).mutationChance;
  return {
    plumage: crossPair<PlumageAllele>(sire.plumage, dam.plumage, 'F', 'f', rng, mutation),
    eggColor: crossPair<EggColorAllele>(
      sire.eggColor,
      dam.eggColor,
      'O',
      'o',
      rng,
      mutation,
    ),
    toes: crossPair<ToeAllele>(sire.toes, dam.toes, 'P', 'p', rng, mutation),
    temperament: crossPair<TemperamentAllele>(
      sire.temperament,
      dam.temperament,
      'D',
      'd',
      rng,
      mutation,
    ),
    broodiness: crossPair<BroodinessAllele>(
      sire.broodiness,
      dam.broodiness,
      'B',
      'b',
      rng,
      mutation,
    ),
    // Polygenic: parents' average plus noise, per GENETICS.md §2.
    brownCoating: round2(
      clamp((sire.brownCoating + dam.brownCoating) / 2 + rng.range(-12, 12), 0, 100),
    ),
  };
}

// --- Phenotype expression ---

export function hasDominant<T extends string>(pair: AllelePair<T>, dominant: T): boolean {
  return pair[0] === dominant || pair[1] === dominant;
}

/** ff — fur-like plumage, not waterproof. The Silkie's defining vulnerability. */
export function isSilkieFeathered(genotype: Genotype): boolean {
  return !hasDominant(genotype.plumage, 'F');
}

export function toeCount(genotype: Genotype): 4 | 5 {
  return hasDominant(genotype.toes, 'P') ? 5 : 4;
}

/** dd — flighty. Triples the escape roll (GENETICS.md §2). */
export function isFlighty(genotype: Genotype): boolean {
  return !hasDominant(genotype.temperament, 'D');
}

export function isBroodyTendency(genotype: Genotype): boolean {
  return hasDominant(genotype.broodiness, 'B');
}

/** BB sits far more readily than Bb — 45% per day vs a modest tendency. */
export function dailyBroodyChance(genotype: Genotype): number {
  const [a, b] = genotype.broodiness;
  if (a === 'B' && b === 'B') return 0.45;
  if (a === 'B' || b === 'B') return 0.15;
  return 0.01;
}

const BROWN_COATING_VISIBLE = 25;
const BROWN_COATING_STRONG = 60;

/**
 * Shell colour = base (blue if O present, else white) + brown coating strength.
 * This is the real mechanism, and the reason blue + brown reads as olive.
 */
export function eggShellColor(genotype: Genotype): EggShellColor {
  const blueBase = hasDominant(genotype.eggColor, 'O');
  const coating = genotype.brownCoating;
  if (blueBase) {
    if (coating >= BROWN_COATING_STRONG) return 'olive';
    if (coating >= BROWN_COATING_VISIBLE) return 'green';
    return 'blue';
  }
  if (coating >= BROWN_COATING_STRONG) return 'brown';
  if (coating >= BROWN_COATING_VISIBLE) return 'cream';
  return 'white';
}

/** Rare shells pay more — the genetics chase has an economic payoff. */
export function eggSalePrice(shell: EggShellColor): number {
  return shell === 'blue' || shell === 'green' || shell === 'olive' ? 8 : 5;
}

/** Effective docility blends the breed baseline with the bird's own genotype. */
export function docilityOf(breed: Breed, genotype: Genotype): number {
  const base = breedProfile(breed).docility;
  return clamp(isFlighty(genotype) ? base * 0.4 : base, 0, 100);
}

export function flightinessOf(breed: Breed, genotype: Genotype): number {
  const base = breedProfile(breed).flightiness;
  // dd is the +300% fence-jumping genotype from GENETICS.md §2.
  return clamp(isFlighty(genotype) ? base * 4 : base, 0, 100);
}

/** A breed-typical genotype, with seeded variation so birds differ. */
export function genotypeForBreed(breed: Breed, rng: Rng): Genotype {
  const maybe = <T extends string>(dominant: T, recessive: T, pDominant: number): [T, T] => [
    rng.chance(pDominant) ? dominant : recessive,
    rng.chance(pDominant) ? dominant : recessive,
  ];

  switch (breed) {
    case 'silkie':
      return {
        plumage: ['f', 'f'],
        eggColor: maybe<EggColorAllele>('O', 'o', 0.15),
        toes: ['P', 'P'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.95),
        broodiness: ['B', 'B'],
        brownCoating: round2(rng.range(10, 35)),
      };
    case 'leghorn':
      return {
        plumage: ['F', 'F'],
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        // Leghorns are the fence-jumpers: usually dd.
        temperament: maybe<TemperamentAllele>('D', 'd', 0.15),
        broodiness: ['b', 'b'],
        brownCoating: round2(rng.range(0, 10)),
      };
    case 'rhode_island_red':
      return {
        plumage: ['F', 'F'],
        eggColor: maybe<EggColorAllele>('O', 'o', 0.05),
        toes: ['p', 'p'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.7),
        // BRD-06 puts the Rhode Island Red at 5-10% broody, and config ships 8.
        // p = 1 - sqrt(1 - 0.08) makes the carrier share match instead of
        // handing half the flock a B allele.
        broodiness: maybe<BroodinessAllele>('B', 'b', 0.041),
        brownCoating: round2(rng.range(55, 85)),
      };
    case 'australorp':
      return {
        plumage: ['F', 'F'],
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.85),
        // Broody-prone but not a Silkie (BRD-35 gives no percentage). p = 0.1
        // makes ~19% of hens carry B, matching the design value of 20.
        broodiness: maybe<BroodinessAllele>('B', 'b', 0.1),
        brownCoating: round2(rng.range(45, 70)),
      };
    case 'isa_brown':
      return {
        plumage: ['F', 'F'],
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.7),
        broodiness: ['b', 'b'],
        brownCoating: round2(rng.range(65, 90)),
      };
    case 'brahma':
      return {
        plumage: ['F', 'F'],
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.4),
        // BRD-35 names the Brahma among breeds that go broody but publishes no
        // percentage, so 15 is the design value; p = 1 - sqrt(1 - 0.15) holds the
        // genotype to it.
        broodiness: maybe<BroodinessAllele>('B', 'b', 0.078),
        brownCoating: round2(rng.range(40, 65)),
      };
    case 'plymouth_rock':
      return {
        plumage: ['F', 'F'],
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        temperament: maybe<TemperamentAllele>('D', 'd', 0.85),
        // BRD-12 puts the Barred Rock at 15-20% broody and config ships 20.
        // p = 1 - sqrt(1 - 0.2) makes the carrier share match.
        broodiness: maybe<BroodinessAllele>('B', 'b', 0.106),
        brownCoating: round2(rng.range(55, 80)),
      };
    case 'buff_orpington':
      return {
        // BRD-28 gives the Orpington gold `s+` at the S locus, which is a
        // different gene from the extended-black F this model tracks, so it has
        // no allele to sit in here; F stands in as the wild-type plumage the
        // other non-black-patterned breeds use. BRD-28's single comb is likewise
        // not modelled — the game has no comb gene.
        plumage: ['F', 'F'],
        // BRD-13: light brown shells, so a white base under a strong coating.
        eggColor: ['o', 'o'],
        toes: ['p', 'p'],
        // BRD-15: placid, non-flighty, 3-4 ft of fence holds her.
        temperament: maybe<TemperamentAllele>('D', 'd', 0.9),
        // BRD-15 puts the Orpington at 30-50% broody and config ships 40.
        // p = 1 - sqrt(1 - 0.4) makes the carrier share match.
        broodiness: maybe<BroodinessAllele>('B', 'b', 0.225),
        brownCoating: round2(rng.range(55, 80)),
      };
  }
}

export interface PunnettOutcome<T extends string> {
  genotype: [T, T];
  probability: number;
}

/**
 * Every offspring genotype for one trait with its probability — the data behind
 * the Punnett-square preview. Mutation is ignored here: the square teaches
 * inheritance, and the 1.5% flip is the surprise that makes hatching fun.
 */
export function punnett<T extends string>(
  sire: AllelePair<T>,
  dam: AllelePair<T>,
): PunnettOutcome<T>[] {
  const counts = new Map<string, { genotype: [T, T]; count: number }>();
  for (const s of sire) {
    for (const d of dam) {
      // Aa and aA are the same genotype; sort so they merge.
      const genotype = ([s, d].sort() as [T, T]);
      const key = genotype.join('');
      const entry = counts.get(key) ?? { genotype, count: 0 };
      entry.count++;
      counts.set(key, entry);
    }
  }
  return [...counts.values()].map(({ genotype, count }) => ({
    genotype,
    probability: count / 4,
  }));
}
